/**
 * Sound effects — Web-Audio synthesized chimes and dings.
 *
 * The kind you hear when Resend confirms a domain or when Stripe
 * acknowledges a payment: two soft notes, ascending, tight decay.
 * Synthesized in code so we don't ship audio assets and the cue
 * still plays when the apparatus is offline.
 *
 * Honors a user-toggleable mute (localStorage 'emspcr-sounds-muted')
 * AND prefers-reduced-motion (the audio analog — if the OS is
 * tuned to "reduce" feedback effects, we honor it).
 *
 * Usage:
 *   import { playSubmitChime } from '@/lib/sound-effects';
 *   await playSubmitChime();
 */

const MUTE_KEY = 'emspcr-sounds-muted';

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    if (muted) localStorage.setItem(MUTE_KEY, '1');
    else localStorage.removeItem(MUTE_KEY);
  } catch { /* ignore */ }
}

let _ctx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  // Some browsers prefix AudioContext (older Safari). Use the
  // standard with a webkit fallback.
  type AudioCtor = typeof AudioContext;
  const W = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  const Ctor = W.AudioContext ?? W.webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
  } catch {
    return null;
  }
  return _ctx;
}

/**
 * Play a single tone with an attack-decay envelope. Internal helper
 * for composing the two-note chime below. Uses a sine wave fundamental
 * + a soft triangle harmonic so the result reads as "pleasant" not
 * "system notification."
 */
function playTone(
  ctx: AudioContext,
  freq: number,
  startAt: number,
  duration: number,
  peakGain = 0.18,
): void {
  const fund = ctx.createOscillator();
  fund.type = 'sine';
  fund.frequency.value = freq;

  const harm = ctx.createOscillator();
  harm.type = 'triangle';
  harm.frequency.value = freq * 2; // octave for warmth

  const fundGain = ctx.createGain();
  const harmGain = ctx.createGain();
  fundGain.gain.value = 0;
  harmGain.gain.value = 0;

  // ADSR envelope — quick attack (8 ms), modest sustain, exponential decay.
  const attack = 0.008;
  const release = duration - attack;
  fundGain.gain.setValueAtTime(0, startAt);
  fundGain.gain.linearRampToValueAtTime(peakGain, startAt + attack);
  fundGain.gain.exponentialRampToValueAtTime(0.0001, startAt + attack + release);

  harmGain.gain.setValueAtTime(0, startAt);
  harmGain.gain.linearRampToValueAtTime(peakGain * 0.35, startAt + attack);
  harmGain.gain.exponentialRampToValueAtTime(0.0001, startAt + attack + release * 0.7);

  fund.connect(fundGain).connect(ctx.destination);
  harm.connect(harmGain).connect(ctx.destination);

  fund.start(startAt);
  harm.start(startAt);
  fund.stop(startAt + duration + 0.05);
  harm.stop(startAt + duration + 0.05);
}

/**
 * Submit-success chime — the satisfying "ding" played when a PCR
 * posts cleanly. Two ascending notes (E5 → A5), each ~280 ms, with
 * the second starting partway through the first so they overlap
 * pleasantly. Plays once; safe to call from any event handler.
 *
 * No-op when:
 *   - User has muted via setMuted(true)
 *   - Browser denies an AudioContext (rare; old browsers)
 *   - User prefers reduced motion (the auditory equivalent — assume
 *     they don't want audio feedback either)
 */
export async function playSubmitChime(): Promise<void> {
  if (isMuted()) return;
  if (typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  const ctx = getCtx();
  if (!ctx) return;
  // Browsers require a user gesture before AudioContext can play.
  // resume() is a no-op if already running.
  try { await ctx.resume(); } catch { /* ignore */ }
  const t0 = ctx.currentTime + 0.005;
  // E5 (659.25 Hz) → A5 (880 Hz) — open, bright fifth-equivalent
  // interval that reads as "complete / confirmed."
  playTone(ctx, 659.25, t0,        0.28, 0.18);
  playTone(ctx, 880.00, t0 + 0.16, 0.32, 0.20);
}

/**
 * Soft tap acknowledgment — for less-significant successes (e.g.,
 * a save-to-draft or a non-final tap). Single short note, lower
 * volume. Currently unused but exposed so future surfaces can
 * differentiate.
 */
export async function playTap(): Promise<void> {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  try { await ctx.resume(); } catch { /* ignore */ }
  const t0 = ctx.currentTime + 0.005;
  playTone(ctx, 1046.50, t0, 0.12, 0.08); // C6, 120 ms, quiet
}
